import { Button } from 'app/components/ui/button'
import type { SendTimesSettings, Weekday } from 'app/types/workflows'
import { LEGAL_SEND_END, LEGAL_SEND_START, WEEKDAYS, clampToLegalSendWindow } from 'app/lib/workflows/workflowSettings'
import { Field, TextField, ToggleField } from './fields'

type SendTimesEditorProps = {
  value: SendTimesSettings
  onChange: (patch: Partial<SendTimesSettings>) => void
}

/** Editor for the workflow-level send window. Lives in the workflow settings panel (one place),
 *  never inside a step. Flat fields, explanation behind the "i", one consequence line when the
 *  window is off because that choice silently drops texts. */
export function SendTimesEditor({ value, onChange }: SendTimesEditorProps) {
  const toggleDay = (day: Weekday) =>
    onChange({ days: value.days.includes(day) ? value.days.filter((d) => d !== day) : [...value.days, day] })

  return (
    <>
      <ToggleField
        label="Limit to a window"
        info="Texting outside 8:00 AM to 8:00 PM on the location's clock is refused by the engine, and a refused workflow send is skipped, not retried. The window holds late sends until it opens."
        // Consequence, not background: with the window off, a text due at 2 AM is lost.
        hint={value.restrict ? undefined : `Late sends are skipped, not held until morning.`}
        checked={value.restrict}
        onChange={(restrict) => onChange({ restrict })}
      />
      {value.restrict && (
        <>
          <Field label="Between" info="Stays inside the legal 8:00 AM to 8:00 PM window; values outside it snap back.">
            <div className="flex items-center gap-2">
              {/* Native type=time gives segmented type-and-auto-advance; hide the clock-picker indicator
                  so it reads as a plain formatted text field, not the native wheel dropdown. */}
              <TextField
                inline
                type="time"
                min={LEGAL_SEND_START}
                max={LEGAL_SEND_END}
                className="[&::-webkit-calendar-picker-indicator]:hidden"
                value={value.start}
                onChange={(e) => onChange({ start: e.target.value })}
                onBlur={() => onChange({ start: clampToLegalSendWindow(value.start, LEGAL_SEND_START) })}
              />
              <span className="text-xs text-minimal">to</span>
              <TextField
                inline
                type="time"
                min={LEGAL_SEND_START}
                max={LEGAL_SEND_END}
                className="[&::-webkit-calendar-picker-indicator]:hidden"
                value={value.end}
                onChange={(e) => onChange({ end: e.target.value })}
                onBlur={() => onChange({ end: clampToLegalSendWindow(value.end, LEGAL_SEND_END) })}
              />
            </div>
          </Field>
          <Field label="Days" error={value.days.length === 0 ? `Pick at least one day.` : undefined}>
            <div className="flex gap-1.5">
              {WEEKDAYS.map((day) => {
                const on = value.days.includes(day.value)
                return (
                  <Button
                    key={day.value}
                    variant="toggle"
                    size="xs"
                    title={day.label}
                    aria-pressed={on}
                    onClick={() => toggleDay(day.value)}
                    className="h-8 flex-1 px-0 font-semibold"
                  >
                    {day.short}
                  </Button>
                )
              })}
            </div>
          </Field>
          <ToggleField
            label="Contact's time zone"
            info="Off, the practice's time zone is used for every contact."
            checked={value.contactTimezone}
            onChange={(contactTimezone) => onChange({ contactTimezone })}
          />
        </>
      )}
    </>
  )
}
